import { useCallback, useEffect, useMemo, useState } from "react";
import apiClient from "../../../services/apiClient";
import { normalizeApiError } from "../../../utils/apiError";
import alertService from "../../../utils/alertService";

function normalizeListPayload(payload = {}, query = {}) {
  const root = payload?.data || payload;

  let rows = [];
  if (Array.isArray(root)) {
    rows = root;
  } else if (root && typeof root === "object") {
    rows = root.data || root.items || root.rows || [];
  }

  const meta = payload?.meta || (typeof root === "object" && !Array.isArray(root) ? root.meta : {}) || {};

  const page = Number(meta?.page || query.page || 1);
  const limit = Number(meta?.limit || query.limit || 50);
  const totalItems = Number(meta?.total || meta?.totalItems || rows.length || 0);
  const totalPages = Number(meta?.totalPages || Math.ceil(totalItems / limit) || 1);

  return {
    rows: Array.isArray(rows) ? rows : [],
    pagination: { page, limit, totalItems, totalPages },
  };
}

function buildUrl(endpoint, id) {
  if (typeof endpoint === "function") {
    return endpoint(id);
  }
  return endpoint;
}

function resolveItemId(item, idKey) {
  if (!item || typeof item !== "object") return undefined;

  if (idKey && item[idKey] !== undefined && item[idKey] !== null && item[idKey] !== "") {
    return item[idKey];
  }

  const fallbackKeys = ["id", "userId", "roleId", "menuId", "_id", "uuid"];
  for (const key of fallbackKeys) {
    if (item[key] !== undefined && item[key] !== null && item[key] !== "") {
      return item[key];
    }
  }

  return undefined;
}

function useCrud({
  endpoints,
  idKey,
  initialFilters = {},
  mapEditItem = (payload) => payload?.data || payload || {},
  transformCreateValues = (values) => values,
  transformEditValues = (values) => values,
  updateMethod = "put",
  queryKeys = {
    page: "page",
    limit: "limit",
    sort: "sort",
    order: "order",
  },
  initialSort = { key: "id", order: "ASC" },
  enabled = true,
}) {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    totalItems: 0,
    totalPages: 1,
  });

  const [filters, setFilters] = useState({
    ...initialFilters,
    [queryKeys.page]: 1,
    [queryKeys.limit]: 50,
    [queryKeys.sort]: initialSort.key,
    [queryKeys.order]: initialSort.order,
  });

  const queryParams = useMemo(() => {
    const params = { ...filters };
    delete params.totalItems;
    delete params.totalPages;

    // Clean empty values to avoid sending unusable filters (like partyId="")
    return Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }, [filters]);

  const fetchList = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(endpoints.list, { params: queryParams });
      const parsed = normalizeListPayload(response?.data || {}, queryParams);
      setRows(parsed.rows);
      setPagination(parsed.pagination);
    } catch (err) {
      const normalized = normalizeApiError(err);
      setError(normalized.message);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [endpoints.list, queryParams, enabled]);

  useEffect(() => {
    if (enabled) {
      fetchList();
    } else {
      setRows([]);
      setPagination({
        page: 1,
        limit: 50,
        totalItems: 0,
        totalPages: 1,
      });
    }
  }, [fetchList, enabled]);

  const changePage = (page) => {
    if (page <= 0) return;
    setFilters((current) => ({ ...current, [queryKeys.page]: page }));
  };

  const changeSort = (key) => {
    setFilters((current) => {
      const currentOrder = current[queryKeys.order] || "ASC";
      const nextOrder = current[queryKeys.sort] === key && currentOrder === "ASC" ? "DESC" : "ASC";
      return {
        ...current,
        [queryKeys.page]: 1,
        [queryKeys.sort]: key,
        [queryKeys.order]: nextOrder,
      };
    });
  };

  const updateFilters = (next) => {
    setFilters((current) => ({ ...current, ...next }));
  };

  const openCreate = () => {
    setModalMode("create");
    setSelectedItem(null);
    setIsModalOpen(true);
  };

  const openEdit = async (item) => {
    setModalMode("edit");
    const selectedId = resolveItemId(item, idKey);
    if (!selectedId) {
      alertService.error("Record ID is required");
      return;
    }

    if (!endpoints.edit) {
      setSelectedItem(item);
      setIsModalOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiClient.get(buildUrl(endpoints.edit, selectedId));
      const fullItem = mapEditItem(response?.data || {});
      setSelectedItem(fullItem);
      setIsModalOpen(true);
    } catch (err) {
      const normalized = normalizeApiError(err);
      alertService.error(normalized.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const save = async (values) => {
    try {
      if (modalMode === "edit") {
        const result = await alertService.editConfirm();
        if (!result.isConfirmed) return;
      }

      setIsSubmitting(true);
      if (modalMode === "edit") {
        const selectedId = resolveItemId(selectedItem, idKey);
        if (!selectedId) throw new Error("Record ID is required");
        const editValues = transformEditValues(values);
        const method = String(updateMethod || "put").toLowerCase();
        if (method === "patch") {
          await apiClient.patch(buildUrl(endpoints.update, selectedId), editValues);
        } else {
          await apiClient.put(buildUrl(endpoints.update, selectedId), editValues);
        }
        alertService.success("Record updated successfully.");
      } else {
        const createValues = transformCreateValues(values);
        await apiClient.post(endpoints.create, createValues);
        alertService.success("Record created successfully.");
      }
      closeModal();
      await fetchList();
    } catch (err) {
      const normalized = normalizeApiError(err);
      alertService.error(normalized.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const askDelete = async (item) => {
    const itemName = item?.name || item?.fullName || item?.lotNo || item?.stockCategoryName || item?.menuName || item?.countryName || item?.partyName || "this record";
    const result = await alertService.deleteConfirm(itemName);
    if (result.isConfirmed) {
      await confirmDelete(item);
    }
  };

  const cancelDelete = () => {
    setDeleteError(null);
    setDeleteTarget(null);
  };

  const confirmDelete = async (item) => {
    const target = item || deleteTarget;
    const deleteId = resolveItemId(target, idKey);
    if (!deleteId) {
      alertService.error("Record ID is required");
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.delete(buildUrl(endpoints.delete, deleteId));
      alertService.success("Record deleted successfully.");
      setDeleteTarget(null);
      await fetchList();
    } catch (err) {
      const normalized = normalizeApiError(err);
      alertService.error(normalized.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    rows,
    isLoading,
    isSubmitting,
    error,
    filters,
    pagination,
    sortConfig: {
      key: filters[queryKeys.sort],
      order: filters[queryKeys.order],
    },
    modalMode,
    isModalOpen,
    selectedItem,
    deleteTarget,
    deleteError,
    openCreate,
    openEdit,
    closeModal,
    askDelete,
    cancelDelete,
    confirmDelete,
    save,
    changePage,
    changeSort,
    updateFilters,
    refresh: fetchList,
    setNotification: () => {}, // No-op for compatibility
  };
}

export default useCrud;
